"use client"
import React, { useState, useEffect, useMemo, useRef } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import Navbar from "../../components/account/Navbar"
import BuddiesGridTab from "../../components/social/BuddiesGridTab"
import PostFeed from "../../components/social/posts/PostFeed"
import PostModal from "../../components/modals/PostModal"
import { userAPI, channelsAPI, postsAPI, socialUtils, socialAPI, clinicAPI, reportsAPI, petAPI, handleApiError } from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import { usePostModal } from "../../contexts/PostModalContext"
import { useTranslation } from "react-i18next"
import { resolveImageUrl, getUserPlaceholderImage, cacheUserGender } from "../../utils/imageUtils"
import UserAverageRating from "../../components/social/UserAverageRating"
import "./userProfileStyle.css"

const OtherUsersProfile = () => {
  const { userType, profileId, userId, postId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const { user: currentUser, isAuthenticated, updateUserData } = useAuth()
  const { openPostModal, closePostModal, isOpen: isPostModalOpen, selectedPostId } = usePostModal()
  const isRTL = i18n.dir() === "rtl"

  const prevIsPostModalOpen = useRef(isPostModalOpen);

  // Helper function to normalize user type
  const normalizeUserType = (userTypeValue) => {
    if (!userTypeValue) return null
    const normalized = String(userTypeValue).toUpperCase()
    // Map legacy and lowercase variants to SERVICE_PROVIDER
    if (normalized === 'PET_SHOP_OWNER' || normalized === 'SERVICE_PROVIDER' ||
      userTypeValue === 'service_provider' || userTypeValue === 'pet_shop_owner') {
      return 'SERVICE_PROVIDER'
    }
    return normalized
  }

  // Use whichever parameter is available
  const targetUserId = profileId || userId

  // Tab mapping
  const TAB_MAP = useMemo(() => ({
    'profile': 1,
    'buddies': 2,
    'pets': 3,
    'communities': 4,
    'media': 5,
    'posts': 6
  }), []);

  const REVERSE_TAB_MAP = useMemo(() => ({
    1: 'profile',
    2: 'buddies',
    3: 'pets',
    4: 'communities',
    5: 'media',
    6: 'posts'
  }), []);

  // Active tab (1-6) - initialize from URL path
  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname;
    const parts = path.split('/');
    if (parts.includes('buddies')) return 2;
    if (parts.includes('pets')) return 3;
    if (parts.includes('communities')) return 4;
    if (parts.includes('media')) return 5;
    if (parts.includes('posts')) return 6;
    return 1;
  })

  // Profile data
  const [profileData, setProfileData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Connection status
  const [connectionStatus, setConnectionStatus] = useState('none')
  const [isFollowing, setIsFollowing] = useState(false)
  const [isPendingFollow, setIsPendingFollow] = useState(false)
  const [confirmRequest, setConfirmRequest] = useState(false)
  const [isBlockedUser, setIsBlockedUser] = useState(false)

  // Communities
  const [communities, setCommunities] = useState([])
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false)
  const [communitiesCount, setCommunitiesCount] = useState(0)

  // Clinics count (for veterinarians)
  const [clinicsCount, setClinicsCount] = useState(0)

  // Posts and media for Tab 2 and Tab 3
  const [posts, setPosts] = useState([])
  const [allPosts, setAllPosts] = useState([]) // Store all fetched posts
  const [profilePosts, setProfilePosts] = useState(null) // Raw posts from profile response (from user-profile endpoint)
  const [mediaItems, setMediaItems] = useState([])
  const [isLoadingMedia, setIsLoadingMedia] = useState(false)
  const [isLoadingPosts, setIsLoadingPosts] = useState(false)
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false)
  const [hasMorePosts, setHasMorePosts] = useState(false)
  const [postsPerPage] = useState(10) // Server-side pagination: 10 posts per page
  const [currentPostsPage, setCurrentPostsPage] = useState(1)

  // Pagination metadata from backend
  const [postsPagination, setPostsPagination] = useState(null)
  const [petsPagination, setPetsPagination] = useState(null)
  const [currentPetsPage, setCurrentPetsPage] = useState(1)
  const [isLoadingMorePets, setIsLoadingMorePets] = useState(false)
  const [hasMorePets, setHasMorePets] = useState(false)

  // Bio expansion state
  const [isBioExpanded, setIsBioExpanded] = useState(false)

  // Success message state
  const [successMessage, setSuccessMessage] = useState(null)

  // Connects modal state
  const [isConnectsModalOpen, setIsConnectsModalOpen] = useState(false)

  // Modals
  const [showUnfollowModal, setShowUnfollowModal] = useState(false)
  const [showConfirmRequestModal, setShowConfirmRequestModal] = useState(false)
  const [showBlockModal, setShowBlockModal] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportReason, setReportReason] = useState('')
  const [reportDetails, setReportDetails] = useState('')
  const [isSubmittingReport, setIsSubmittingReport] = useState(false)
  const [reportReasonsList, setReportReasonsList] = useState([])
  const [isLoadingReportReasons, setIsLoadingReportReasons] = useState(false)

  // Report Modal Steps State
  const [currentStep, setCurrentStep] = useState(1)
  const [blockUser, setBlockUser] = useState(false)
  const [muteUser, setMuteUser] = useState(false)

  // Tab animation direction
  const [tabDirection, setTabDirection] = useState('right')

  // PostModal state (for creating/editing posts)
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState(false)
  const [editingPostData, setEditingPostData] = useState(null)
  const [selectedPrivacy, setSelectedPrivacy] = useState("everyone")
  const [pendingFileType, setPendingFileType] = useState(null)

  // Apply connection status state
  const applyConnectionStatusState = (status) => {
    const normalizedStatus = status || 'none'
    setConnectionStatus(normalizedStatus)

    switch (normalizedStatus) {
      case 'friends':
        setIsFollowing(true)
        setIsPendingFollow(false)
        setConfirmRequest(false)
        break
      case 'pending_incoming':
        setIsFollowing(false)
        setIsPendingFollow(false)
        setConfirmRequest(true)
        break
      case 'pending_outgoing':
        setIsFollowing(false)
        setIsPendingFollow(true)
        setConfirmRequest(false)
        break
      default:
        setIsFollowing(false)
        setIsPendingFollow(false)
        setConfirmRequest(false)
        break
    }
  }

  // Helper function to check if current user can send follow request
  const canSendFollowRequest = () => {
    if (profileData?.has_blocked_you || isBlockedUser) {
      return false
    }

    const currentConnectionStatus = profileData?.connectionStatus || connectionStatus

    if (currentConnectionStatus === 'friends' || isFollowing) {
      return true
    }

    if (currentConnectionStatus === 'pending_incoming' || confirmRequest) {
      return true
    }

    if (currentConnectionStatus === 'pending_outgoing' || isPendingFollow) {
      return true
    }

    const privacySettings = profileData?.privacy_settings
    if (!privacySettings) {
      return true
    }

    const whoCanSendFollowRequests = privacySettings.who_can_send_me_follow_requests || 'everyone'

    switch (whoCanSendFollowRequests) {
      case 'everyone':
        return true
      case 'no_one':
        return false
      case 'friends':
        return false
      case 'friends_of_friends':
        const mutualConnections = profileData?.mutualConnections || []
        return mutualConnections.length > 0
      default:
        return true
    }
  }

  // Helper function to check if current user can send message
  const canSendMessage = () => {
    if (profileData?.has_blocked_you || isBlockedUser) {
      return false
    }

    const privacySettings = profileData?.privacy_settings
    if (!privacySettings) {
      return true
    }

    const whoCanSendMessages = privacySettings.who_can_send_messages || 'everyone'

    switch (whoCanSendMessages) {
      case 'everyone':
        return true
      case 'no_one':
        return false
      case 'friends':
        const currentConnectionStatus = profileData?.connectionStatus || connectionStatus
        return currentConnectionStatus === 'friends' || isFollowing
      case 'friends_of_friends':
        return true
      default:
        return true
    }
  }

  // Helper function to check if current user can see buddies
  const canSeeBuddies = () => {
    const privacySettings = profileData?.privacy_settings
    if (!privacySettings) {
      return true
    }

    const whoCanSeeMyBuddies = privacySettings.who_can_see_my_buddies || 'everyone'

    switch (whoCanSeeMyBuddies) {
      case 'everyone':
        return true
      case 'no_one':
        return false
      case 'friends':
        const currentConnectionStatus = profileData?.connectionStatus || connectionStatus
        return currentConnectionStatus === 'friends' || isFollowing
      case 'friends_of_friends':
        const mutualConnections = profileData?.mutualConnections || []
        const isFriend = (profileData?.connectionStatus || connectionStatus) === 'friends' || isFollowing
        return isFriend || mutualConnections.length > 0
      default:
        return true
    }
  }

  // Fetch user profile data
  useEffect(() => {
    // If we're on other user profile but it's actually the current user, redirect
    const currentUserId = currentUser?.id || currentUser?.user_id;
    if (currentUserId && targetUserId && currentUserId.toString() === targetUserId.toString()) {
      navigate('/social/profile');
      return;
    }

    const loadProfileData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const token = localStorage.getItem("access_token")
        if (!token) {
          throw new Error("Authentication required")
        }

        if (!targetUserId) {
          throw new Error("User ID is required")
        }

        const userIdParam = parseInt(targetUserId.trim(), 10)
        if (isNaN(userIdParam)) {
          throw new Error(`Invalid user ID: ${targetUserId}`)
        }

        const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

        // Fetch other user's profile with pagination parameters
        const profileResponse = await fetch(
          `${API_BASE_URL}/api/v1/user-profile/${userIdParam}?posts_page=1&posts_per_page=${postsPerPage}&pets_page=1&pets_per_page=10`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        )

        if (!profileResponse.ok) {
          throw new Error("Failed to fetch profile data")
        }

        const profileResult = await profileResponse.json()

        if (profileResult.status !== "success" || !profileResult.data) {
          throw new Error("Invalid profile data received")
        }

        let userData = profileResult.data
        const connectionStatusFromApi = userData.connection_status || 'none'


        // Bio and user_type are already included in the main profile response
        const bioValue = userData.bio || userData.description || userData.about || userData.about_me || "";

        // Store pagination metadata from backend
        if (userData.posts_pagination) {
          setPostsPagination(userData.posts_pagination)
          setHasMorePosts(userData.posts_pagination.has_more_pages || false)
        }
        if (userData.pets_pagination) {
          setPetsPagination(userData.pets_pagination)
          setHasMorePets(userData.pets_pagination.has_more_pages || false)
        }

        // Extract data from the API response
        const postsData = userData.posts || []
        let petsData = userData.pets || []
        const mediaData = userData.media || []
        const mutualConnectionsData = userData.mutual_connections || []

        // Fallback: If no pets found in profile data, try fetching them explicitly
        if (!petsData || petsData.length === 0) {
          try {
            console.log(`⚠️ No pets in profile data, fetching explicitly for user ${userIdParam}...`);
            const petsResponse = await petAPI.getOtherPets(userIdParam);
            if (petsResponse.data && petsResponse.data.status === 'success') {
              petsData = petsResponse.data.data || [];
              console.log(`✅ Fetched ${petsData.length} pets via fallback API`);
            }
          } catch (petErr) {
            console.error("Error fetching pets fallback:", petErr);
          }
        }

        // Store raw posts from profile response for use in loadInitialPosts
        // These posts come FROM the user-profile endpoint, not a separate API call
        if (postsData && Array.isArray(postsData) && postsData.length > 0) {
          setProfilePosts(postsData)
          console.log(`✅ Stored ${postsData.length} posts from user-profile response in profilePosts state`)
        } else {
          setProfilePosts(null)
          console.warn(`⚠️ No posts in user-profile response. postsData:`, postsData)
        }

        // Unified connects (mutual friends) count
        const connectsCount =
          userData.friends_count ??
          userData.connections_count ??
          userData.connects_count ??
          userData.followers_count ??
          userData.following_count ??
          0

        // Calculate posts count - use pagination total if available
        const actualPostsCount = userData.posts_pagination?.total || postsData.length || 0

        // Bio and user_type are already included in the main profile response or can be extracted from userData
        // (bioValue was already calculated at line 290)


        // Average rating calculation fallback
        let avgRating = userData.average_rating ?? userData.averageRating ?? 0;
        let countRating = userData.ratings_count ?? userData.ratingsCount ?? 0;

        // Fallback: Calculate from posts if backend rating is missing/zero but posts exist
        // Note: We check for 0/null because backend might return 0 even if posts have ratings (sync issue)
        if ((avgRating === null || avgRating === 0 || countRating === 0) && postsData && Array.isArray(postsData) && postsData.length > 0) {
          const ratedPosts = postsData.filter(p =>
            (p.average_rating !== null && p.average_rating !== undefined && parseFloat(p.average_rating) > 0) ||
            (p.avg_rating !== null && p.avg_rating !== undefined && parseFloat(p.avg_rating) > 0)
          );

          if (ratedPosts.length > 0) {
            const totalRating = ratedPosts.reduce((sum, p) =>
              sum + (parseFloat(p.average_rating ?? p.avg_rating) || 0), 0
            );
            avgRating = totalRating / ratedPosts.length;
            countRating = ratedPosts.length;

            console.log(`✅ OtherUsersProfile: Calculated frontend rating fallback: ${avgRating.toFixed(1)} (${countRating} posts)`);
          }
        }

        const transformedData = {
          id: userData.user_id,
          name: userData.user_type === 'FIRM' ? (userData.display_name || userData.name_en || 'Firm') : (`${userData.first_name || ""} ${userData.last_name || ""}`.trim() || "User"),
          username: userData.username || `@user${userData.user_id}`,
          avatar: userData.profile_image_url || getUserPlaceholderImage(userData),
          bio: bioValue,
          // Use user_type from API, fallback to social endpoint, or null (never use current user's type)
          user_type: normalizeUserType(userData.user_type) || null,
          sub_type: userData.sub_type || userData.subType || null,
          sub_type_display_name: userData.sub_type_display_name || userData.subTypeDisplayName || null,
          // Average rating from backend (calculated from all post ratings) - backwards compatible
          average_rating: avgRating,
          ratings_count: countRating,
          rating_label: userData.rating_label ?? userData.ratingLabel ?? null, // Optional: label from backend
          postsCount: actualPostsCount,
          connectsCount: connectsCount,
          // Stats from backend
          clinicsCount: userData.stats?.clinics_count ?? userData.clinics_count ?? userData.owned_clinics_count ?? userData.stats?.clinics ?? 0,
          communitiesCount: userData.stats?.communities_count ?? 0,
          location: (userData.city_data || userData.country_data) 
            ? [socialUtils.getLocalizedName(userData.city_data), socialUtils.getLocalizedName(userData.country_data)].filter(Boolean).join(', ')
            : (userData.full_address || userData.location || ""),
          contact: userData.phone || null,
          phone: userData.phone || null,
          legal_number: userData.legal_number ?? null,
          name_en: userData.name_en ?? null,
          contact_info: userData.contact_info ?? null,
          address: userData.address ?? null,
          show_name_en: userData.show_name_en ?? true,
          show_legal_number: userData.show_legal_number ?? true,
          show_contact_info: userData.show_contact_info ?? true,
          show_address: userData.show_address ?? true,
          gender: userData.gender || null,
          age: userData.age || null,
          email: userData.email || null,
          // Include posts from profile response (paginated)
          posts: postsData,
          pets: petsData.map(pet => {
            let avatarUrl = "/images/no-image-pet.png"
            if (pet.primary_image_url) {
              avatarUrl = pet.primary_image_url
            } else if (pet.profile_image_path) {
              avatarUrl =
                typeof pet.profile_image_path === "string"
                  ? pet.profile_image_path
                  : pet.profile_image_path?.url ||
                  pet.profile_image_path?.path ||
                  "/images/no-image-pet.png"
            }
            return {
              id: pet.id,
              name: pet.name || t("userprofile.pets.unnamed"),
              avatar: avatarUrl,
              type: pet.species?.name || null,
            }
          }),
          mutualConnections: mutualConnectionsData,
          connectionStatus: connectionStatusFromApi,
          privacy_settings: userData.privacy_settings || {
            who_can_send_me_follow_requests: "everyone",
            who_can_send_messages: "everyone",
          },
          has_blocked_you: userData.has_blocked_you || false,
          is_blocked: userData.is_blocked || false,
        }

        setProfileData(transformedData)
        // Cache gender for correct placeholder images across the app
        if (transformedData.gender && transformedData.id) {
          cacheUserGender(transformedData.id, transformedData.gender)
        }
        setCommunitiesCount(transformedData.communitiesCount)
        setClinicsCount(transformedData.clinicsCount)
        applyConnectionStatusState(connectionStatusFromApi)
        setIsBlockedUser(userData.is_blocked || false)
      } catch (err) {
        console.error("Error loading profile data:", err)
        setError(err.message || "Failed to load profile")
      } finally {
        setIsLoading(false)
      }
    }

    if (isAuthenticated && targetUserId) {
      loadProfileData()
    }
  }, [targetUserId, isAuthenticated])

  // Fetch user's communities (only public ones)
  useEffect(() => {
    const loadCommunities = async () => {
      if (!profileData?.id) return

      try {
        setIsLoadingCommunities(true)
        const token = localStorage.getItem("access_token")
        if (!token) {
          setCommunities([])
          setCommunitiesCount(0)
          return
        }

        const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

        // Try to fetch communities for the viewed user
        // Option 1: Try with user_id parameter
        let response = await fetch(`${API_BASE_URL}/api/v1/channels/my-channels?user_id=${profileData.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })

        if (!response.ok) {
          // Option 2: Try alternative endpoint format
          response = await fetch(`${API_BASE_URL}/api/v1/users/${profileData.id}/channels`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          })
        }

        if (response.ok) {
          const result = await response.json()
          const channelsData = result.data?.data || result.data || []

          // Filter to show only public communities
          const publicCommunities = channelsData
            .filter(channel => {
              const privacy = channel.privacy_setting || channel.privacySetting
              return privacy === 'public'
            })
            .map(channel => {
              const coverImageUrl = resolveImageUrl(
                channel.cover_image_url,
                channel.cover_image,
                channel.coverImageUrl,
                channel.coverImage,
                channel.cover_image_path,
                channel.images?.cover,
                channel.media?.cover
              )
              const profileImageUrl = resolveImageUrl(
                channel.profile_image_url,
                channel.profile_image,
                channel.profileImageUrl,
                channel.profileImage,
                channel.profile_image_path,
                channel.images?.profile,
                channel.media?.profile
              )

              return {
                id: channel.id,
                name: channel.name,
                description: channel.description || "",
                coverImage: coverImageUrl || "/images/social/pic-placeholder.png",
                profileImage: profileImageUrl || "/images/social/pic-placeholder.png",
                memberCount: channel.members_count || channel.memberCount || 0,
                postCount: channel.posts_count || channel.postCount || 0,
              }
            })

          setCommunities(publicCommunities)
          // Only update count if not already set from profile stats (profile stats is more authoritative)
          setCommunitiesCount(prev => prev || publicCommunities.length)
        } else {
          // If endpoint doesn't exist, set empty array
          setCommunities([])
          setCommunitiesCount(0)
        }
      } catch (err) {
        console.error("Error loading communities:", err)
        setCommunities([])
        setCommunitiesCount(0)
      } finally {
        setIsLoadingCommunities(false)
      }
    }

    if (profileData?.id) {
      loadCommunities()
    }
  }, [profileData?.id])

  // Fetch clinics count for veterinarians
  useEffect(() => {
    const loadClinicsCount = async () => {
      // Only fetch for veterinarians
      const viewedUserType = normalizeUserType(profileData?.user_type)
      if (!viewedUserType || (viewedUserType !== 'VETERINARIAN' && viewedUserType !== 'VET')) {
        setClinicsCount(0)
        return
      }

      // First, check if clinics count is already in profileData (from API response)
      // This is the best case - no extra API call needed
      if (profileData?.clinicsCount !== undefined && profileData.clinicsCount !== null) {
        setClinicsCount(profileData.clinicsCount)
        return
      }

      // Fallback: Try to fetch clinics if not in profileData
      try {
        const token = localStorage.getItem("access_token")
        if (!token) {
          setClinicsCount(0)
          return
        }

        // Use clinicAPI to fetch clinics for the viewed user
        try {
          const response = await clinicAPI.getClinicSetupData({ user_id: profileData.id })

          if (response.data?.status === 'success') {
            const clinics = response.data.data?.owned_clinics || []
            const count = Array.isArray(clinics) ? clinics.length : 0
            setClinicsCount(count >= 1 ? count : 0)
          } else {
            setClinicsCount(0)
          }
        } catch (error) {
          // Try alternative endpoint format if clinic-setup-data fails
          try {
            const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
            const response = await fetch(`${API_BASE_URL}/api/v1/users/${profileData.id}/clinics`, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            })

            if (response.ok) {
              const result = await response.json()
              const clinics = result.data?.clinics || []
              const count = Array.isArray(clinics) ? clinics.length : 0
              setClinicsCount(count >= 1 ? count : 0)
            } else {
              setClinicsCount(0)
            }
          } catch (fallbackError) {
            setClinicsCount(0)
          }
        }
      } catch (err) {
        console.error("Error loading clinics count:", err)
        setClinicsCount(0)
      }
    }

    // Load if we have user data and it's the target user's profile
    // But if we already have a count from profile stats, we might not need to fetch
    if (profileData?.id && profileData?.user_type && !profileData.clinicsCount) {
      loadClinicsCount()
    } else if (!profileData) {
      setClinicsCount(0)
    }
  }, [profileData?.id, profileData?.user_type, profileData?.clinicsCount])



  // Initial posts load - use posts from profile response (server-side pagination)
  // Posts come FROM the user-profile response, not from a separate API endpoint
  useEffect(() => {
    const loadInitialPosts = async () => {
      if (!profileData?.id) return

      setIsLoadingPosts(true)
      setIsLoadingMedia(true)
      try {
        // Get posts from profile response (already paginated and included in user-profile response)
        // profilePosts is set from userData.posts in loadProfileData
        // Try profilePosts first, then fallback to profileData.posts
        const postsToUse = profilePosts || profileData?.posts || []
        const postsData = Array.isArray(postsToUse) ? [...postsToUse] : []

        console.log(`📋 Loading posts - profilePosts:`, profilePosts?.length || 0, "profileData.posts:", profileData?.posts?.length || 0, "postsData:", postsData.length)

        if (postsData.length === 0) {
          console.warn("⚠️ No posts in user-profile response. Backend should include posts array when posts_page param is provided.")
          console.warn("⚠️ Full profileData:", profileData)
          console.warn("⚠️ Full profilePosts:", profilePosts)
        } else {
          console.log(`✅ Processing ${postsData.length} posts from user-profile response`)
        }

        if (postsData.length > 0) {
          // Transform posts
          let transformedPosts = postsData
            .map(socialUtils.transformPostData)
            .filter(post => post !== null)

          // Fetch missing parent_post data for reposts (same logic as before)
          const postsWithMissingRepostData = transformedPosts.filter(p =>
            (p.is_repost || p.repost_id || p.type === 'repost' || p.parent_post_id) &&
            (!p.reposted_post || Object.keys(p.reposted_post).length === 0) &&
            (!p.original_post || Object.keys(p.original_post).length === 0) &&
            (p.repost_id || p.parent_post_id)
          )

          if (postsWithMissingRepostData.length > 0) {
            const fetchPromises = postsWithMissingRepostData.map(async (repostPost) => {
              try {
                const originalPostId = repostPost.repost_id || repostPost.parent_post_id
                if (!originalPostId) return

                const originalPostResponse = await postsAPI.getPost(originalPostId)
                let originalPostData = null
                if (originalPostResponse?.data) {
                  if (originalPostResponse.data.status === 'success' && originalPostResponse.data.data) {
                    originalPostData = originalPostResponse.data.data
                  } else if (originalPostResponse.data.data) {
                    originalPostData = originalPostResponse.data.data
                  } else if (originalPostResponse.data.id) {
                    originalPostData = originalPostResponse.data
                  }
                } else if (originalPostResponse?.id) {
                  originalPostData = originalPostResponse
                }

                if (originalPostData) {
                  const transformedOriginalPost = socialUtils.transformPostData(originalPostData)
                  if (transformedOriginalPost && transformedOriginalPost.author) {
                    const author = transformedOriginalPost.author
                    repostPost.reposted_post = {
                      id: transformedOriginalPost.id,
                      author: {
                        id: author.id || 0,
                        name: author.name ||
                          (author.first_name && author.last_name
                            ? `${author.first_name} ${author.last_name}`
                            : author.username || 'Unknown User'),
                        username: author.username || 'unknown',
                        avatar: author.avatar || author.image || getUserPlaceholderImage(author),
                        userType: author.userType || author.user_type || 'PET_GUARDIAN',
                      },
                      content: transformedOriginalPost.content || '',
                      image: transformedOriginalPost.image || null,
                      media: transformedOriginalPost.media || [],
                      timestamp: transformedOriginalPost.timestamp || transformedOriginalPost.created_at,
                      created_at: transformedOriginalPost.created_at,
                      reactions_count: transformedOriginalPost.reactions?.likes || transformedOriginalPost.reactions_count || 0,
                      comments_count: transformedOriginalPost.reactions?.comments || transformedOriginalPost.comments_count || 0,
                      reposts_count: transformedOriginalPost.reposts_count || 0,
                      shares_count: transformedOriginalPost.reactions?.shares || transformedOriginalPost.shares_count || 0,
                      reactions: transformedOriginalPost.reactions || {
                        likes: transformedOriginalPost.reactions_count || 0,
                        comments: transformedOriginalPost.comments_count || 0,
                        shares: transformedOriginalPost.shares_count || 0,
                      },
                    }
                    repostPost.original_post = repostPost.reposted_post
                  }
                }
              } catch (error) {
                console.error(`Error fetching original post for repost ${repostPost.id}:`, error)
              }
            })
            await Promise.all(fetchPromises)
          }

          setPosts(transformedPosts)
          setAllPosts(transformedPosts)
          setCurrentPostsPage(1)

          // Setup media items from posts
          const newMedia = []
          transformedPosts.forEach(post => {
            if (post.media && Array.isArray(post.media)) {
              post.media.forEach(mediaItem => {
                newMedia.push({
                  ...mediaItem,
                  postId: post.id,
                  postContent: post.content,
                  createdAt: post.created_at
                })
              })
            }
          })
          setMediaItems(newMedia)
        } else {
          setPosts([])
          setAllPosts([])
          setMediaItems([])
        }
      } catch (err) {
        console.error("Error loading initial posts:", err)
      } finally {
        setIsLoadingPosts(false)
        setIsLoadingMedia(false)
      }
    }

    if (profileData?.id) {
      loadInitialPosts()
    }
  }, [profileData?.id, profilePosts, targetUserId, postsPerPage])

  // Load more posts handler
  // Load more posts function - fetches next page from API
  const loadMorePosts = async () => {
    if (isLoadingMorePosts || !hasMorePosts || !targetUserId) return

    setIsLoadingMorePosts(true)
    try {
      const token = localStorage.getItem("access_token")
      if (!token) {
        setIsLoadingMorePosts(false)
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const userIdParam = parseInt(targetUserId.trim(), 10)
      const nextPage = currentPostsPage + 1

      // Fetch next page of posts from profile endpoint
      const response = await fetch(
        `${API_BASE_URL}/api/v1/user-profile/${userIdParam}?posts_page=${nextPage}&posts_per_page=${postsPerPage}&pets_page=1&pets_per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (response.ok) {
        const result = await response.json()
        const userData = result.data || result

        if (userData.posts && Array.isArray(userData.posts)) {
          // Transform new posts
          const transformedNewPosts = userData.posts
            .map(socialUtils.transformPostData)
            .filter((post) => post !== null)

          // Fetch missing parent_post data for reposts (same logic as initial load)
          const postsWithMissingRepostData = transformedNewPosts.filter(p =>
            (p.is_repost || p.repost_id || p.type === 'repost' || p.parent_post_id) &&
            (!p.reposted_post || Object.keys(p.reposted_post).length === 0) &&
            (!p.original_post || Object.keys(p.original_post).length === 0) &&
            (p.repost_id || p.parent_post_id)
          )

          if (postsWithMissingRepostData.length > 0) {
            const fetchPromises = postsWithMissingRepostData.map(async (repostPost) => {
              try {
                const originalPostId = repostPost.repost_id || repostPost.parent_post_id
                if (!originalPostId) return

                const originalPostResponse = await postsAPI.getPost(originalPostId)
                let originalPostData = null
                if (originalPostResponse?.data) {
                  if (originalPostResponse.data.status === 'success' && originalPostResponse.data.data) {
                    originalPostData = originalPostResponse.data.data
                  } else if (originalPostResponse.data.data) {
                    originalPostData = originalPostResponse.data.data
                  } else if (originalPostResponse.data.id) {
                    originalPostData = originalPostResponse.data
                  }
                } else if (originalPostResponse?.id) {
                  originalPostData = originalPostResponse
                }

                if (originalPostData) {
                  const transformedOriginalPost = socialUtils.transformPostData(originalPostData)
                  if (transformedOriginalPost && transformedOriginalPost.author) {
                    const author = transformedOriginalPost.author
                    repostPost.reposted_post = {
                      id: transformedOriginalPost.id,
                      author: {
                        id: author.id || 0,
                        name: author.name ||
                          (author.first_name && author.last_name
                            ? `${author.first_name} ${author.last_name}`
                            : author.username || 'Unknown User'),
                        username: author.username || 'unknown',
                        avatar: author.avatar || author.image || getUserPlaceholderImage(author),
                        userType: author.userType || author.user_type || 'PET_GUARDIAN',
                      },
                      content: transformedOriginalPost.content || '',
                      image: transformedOriginalPost.image || null,
                      media: transformedOriginalPost.media || [],
                      timestamp: transformedOriginalPost.timestamp || transformedOriginalPost.created_at,
                      created_at: transformedOriginalPost.created_at,
                      reactions_count: transformedOriginalPost.reactions?.likes || transformedOriginalPost.reactions_count || 0,
                      comments_count: transformedOriginalPost.reactions?.comments || transformedOriginalPost.comments_count || 0,
                      reposts_count: transformedOriginalPost.reposts_count || 0,
                      shares_count: transformedOriginalPost.reactions?.shares || transformedOriginalPost.shares_count || 0,
                      reactions: transformedOriginalPost.reactions || {
                        likes: transformedOriginalPost.reactions_count || 0,
                        comments: transformedOriginalPost.comments_count || 0,
                        shares: transformedOriginalPost.shares_count || 0,
                      },
                    }
                    repostPost.original_post = repostPost.reposted_post
                  }
                }
              } catch (error) {
                console.error(`Error fetching original post for repost ${repostPost.id}:`, error)
              }
            })
            await Promise.all(fetchPromises)
          }

          // Append new posts to existing ones
          setPosts(prevPosts => [...prevPosts, ...transformedNewPosts])
          setAllPosts(prevAllPosts => [...prevAllPosts, ...transformedNewPosts])
          setCurrentPostsPage(nextPage)

          // Update pagination metadata
          if (userData.posts_pagination) {
            setPostsPagination(userData.posts_pagination)
            setHasMorePosts(userData.posts_pagination.has_more_pages || false)
          } else {
            setHasMorePosts(false)
          }

          // Append new media items
          const newMedia = []
          transformedNewPosts.forEach(post => {
            if (post.media && Array.isArray(post.media)) {
              post.media.forEach(mediaItem => {
                newMedia.push({
                  ...mediaItem,
                  postId: post.id,
                  postContent: post.content,
                  createdAt: post.created_at
                })
              })
            }
          })
          setMediaItems(prev => [...prev, ...newMedia])
        }
      }
    } catch (error) {
      console.error("Error loading more posts:", error)
    } finally {
      setIsLoadingMorePosts(false)
    }
  }

  // Load more pets function - fetches next page from API
  const loadMorePets = async () => {
    if (isLoadingMorePets || !hasMorePets || !targetUserId) return

    setIsLoadingMorePets(true)
    try {
      const token = localStorage.getItem("access_token")
      if (!token) {
        setIsLoadingMorePets(false)
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const userIdParam = parseInt(targetUserId.trim(), 10)
      const nextPage = currentPetsPage + 1

      // Fetch next page of pets from profile endpoint
      const response = await fetch(
        `${API_BASE_URL}/api/v1/user-profile/${userIdParam}?posts_page=1&posts_per_page=${postsPerPage}&pets_page=${nextPage}&pets_per_page=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (response.ok) {
        const result = await response.json()
        const userData = result.data || result

        if (userData.pets && Array.isArray(userData.pets)) {
          // Append new pets to existing ones
          setProfileData(prevData => ({
            ...prevData,
            pets: [...(prevData.pets || []), ...userData.pets.map(pet => {
              let avatarUrl = "/images/no-image-pet.png"
              if (pet.primary_image_url) {
                avatarUrl = pet.primary_image_url
              } else if (pet.profile_image_path) {
                avatarUrl =
                  typeof pet.profile_image_path === "string"
                    ? pet.profile_image_path
                    : pet.profile_image_path?.url ||
                    pet.profile_image_path?.path ||
                    "/images/no-image-pet.png"
              }
              return {
                id: pet.id,
                name: pet.name || t("userprofile.pets.unnamed"),
                avatar: avatarUrl,
                type: pet.species?.name || null,
              }
            })]
          }))
          setCurrentPetsPage(nextPage)

          // Update pagination metadata
          if (userData.pets_pagination) {
            setPetsPagination(userData.pets_pagination)
            setHasMorePets(userData.pets_pagination.has_more_pages || false)
          } else {
            setHasMorePets(false)
          }
        }
      }
    } catch (error) {
      console.error("Error loading more pets:", error)
    } finally {
      setIsLoadingMorePets(false)
    }
  }

  // Handle authentication redirect
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate("/auth/login", { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  // Fetch report reasons when modal opens
  useEffect(() => {
    if (showReportModal && reportReasonsList.length === 0) {
      const fetchReportReasons = async () => {
        try {
          setIsLoadingReportReasons(true)
          // Fetch reasons specifically for reporting users
          const response = await reportsAPI.getReasons('user')
          if (response.data && response.data.status === 'success') {
            setReportReasonsList(response.data.data)
          }
        } catch (error) {
          console.error("Failed to fetch report reasons:", error)
          // Fallback to minimal list if API fails
          setReportReasonsList([
            { value: 'spam', reasonText: t("otherProfile.modals.report.reasons.spam") || "Spam" },
            { value: 'harassment', reasonText: t("otherProfile.modals.report.reasons.harassment") || "Harassment" },
            { value: 'other', reasonText: t("otherProfile.modals.report.reasons.other") || "Other" }
          ])
        } finally {
          setIsLoadingReportReasons(false)
        }
      }
      fetchReportReasons()
    }
  }, [showReportModal, t])

  // Listen for connection accepted event to update connection button
  useEffect(() => {
    const handleConnectionAccepted = (event) => {
      const { senderId } = event.detail
      const currentProfileId = profileData?.id || targetUserId
      const currentUserId = currentUser?.id || currentUser?.user_id

      // Check if:
      // 1. The notification is for the current user (we're viewing someone else's profile)
      // 2. The senderId (who accepted) matches the profile we're currently viewing
      if (senderId && currentProfileId && currentUserId &&
        senderId.toString() === currentProfileId.toString() &&
        currentUserId.toString() !== currentProfileId.toString()) {
        // Update connection status to 'friends' (connected)
        applyConnectionStatusState('friends')

        // Force a re-render by updating state
        setIsFollowing(true)
        setIsPendingFollow(false)
        setConfirmRequest(false)

        // Update profile's connects count
        if (profileData) {
          setProfileData(prev => ({
            ...prev,
            connectsCount: (prev.connectsCount || 0) + 1
          }))
        }

        // Update current user's following + friends count
        if (currentUser && updateUserData) {
          const currentFollowing = currentUser.following_count ||
            currentUser.stats?.following ||
            0
          const currentFriends = currentUser.friends_count ||
            currentUser.stats?.friends ||
            0
          updateUserData({
            following_count: currentFollowing + 1,
            friends_count: currentFriends + 1,
            stats: {
              ...(currentUser.stats || {}),
              following: currentFollowing + 1,
              friends: currentFriends + 1
            }
          })
        }
      }
    }

    window.addEventListener('connectionAccepted', handleConnectionAccepted)
    return () => {
      window.removeEventListener('connectionAccepted', handleConnectionAccepted)
    }
  }, [profileData?.id, targetUserId, currentUser, updateUserData])

  // Sync activeTab with URL path changes
  useEffect(() => {
    const path = location.pathname;
    const parts = path.split('/');
    let newTab = 1;
    if (parts.includes('buddies')) newTab = 2;
    else if (parts.includes('pets')) newTab = 3;
    else if (parts.includes('communities')) newTab = 4;
    else if (parts.includes('media')) newTab = 5;
    else if (parts.includes('posts')) newTab = 6;

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.pathname, activeTab]);

  // Sync post modal with URL postId
  useEffect(() => {
    // Don't reopen if modal was just closed (let the URL cleanup useEffect handle it)
    const wasJustClosed = prevIsPostModalOpen.current && !isPostModalOpen;

    // If we have a postId in the URL but the modal isn't open for it, open it
    // But only if the modal wasn't just closed (to prevent reopening after close)
    if (postId && !wasJustClosed && (!isPostModalOpen || String(selectedPostId) !== String(postId))) {
      openPostModal(postId);
    }
    // Don't auto-close modal when there's no postId in URL
    // This allows modal to stay open when opened programmatically (e.g., clicking nested post)
    // Modal will close when user clicks close button or navigates away (handled by other useEffect)
  }, [postId, selectedPostId, isPostModalOpen, openPostModal, closePostModal]);

  // Sync URL with post modal close
  useEffect(() => {
    // If the modal was JUST closed (transition from true to false) 
    // but the postId is still in the URL, that means the user clicked the close button
    if (prevIsPostModalOpen.current && !isPostModalOpen && postId) {
      const basePath = getBaseProfilePath();
      const tabName = REVERSE_TAB_MAP[activeTab];
      const newPath = activeTab === 1 ? basePath : `${basePath}/${tabName}`;
      navigate(newPath);
    }

    // Update the ref for the next render
    prevIsPostModalOpen.current = isPostModalOpen;
  }, [isPostModalOpen, postId, activeTab, navigate, REVERSE_TAB_MAP]);

  // Helper to get the canonical base profile path
  const getBaseProfilePath = () => {
    if (targetUserId) {
      return `/social/profile/${targetUserId}`;
    }
    return '/social/profile';
  };

  // Navigate between tabs
  const goToTab = (tabNumber) => {
    if (tabNumber >= 1 && tabNumber <= 6) {
      setTabDirection(tabNumber > activeTab ? 'left' : 'right')
      setActiveTab(tabNumber)

      // Tab 2 (buddies) - navigate to /buddies path
      if (tabNumber === 2) {
        const basePath = getBaseProfilePath();
        navigate(`${basePath}/buddies`);
        return;
      }

      const tabName = REVERSE_TAB_MAP[tabNumber];
      const basePath = getBaseProfilePath();

      const newPath = tabNumber === 1 ? basePath : `${basePath}/${tabName}`;
      navigate(newPath);
    }
  }

  const nextTab = () => {
    setTabDirection('left')
    setActiveTab(prev => prev < 6 ? prev + 1 : 1)
  }

  const prevTab = () => {
    setTabDirection('right')
    setActiveTab(prev => prev > 1 ? prev - 1 : 6)
  }

  // Post handlers for Tab 3
  // NOTE: On another user's profile we never call the edit API from here.
  // PostReactions / RatingWidget already made their own API calls (rate, react, etc.)
  // and pass back the already-updated partial post object. All we need to do is
  // merge that data into local state so the UI stays in sync.
  // Calling postsAPI.updatePost() here would return 403 (can't edit others' posts),
  // which the auth interceptor might treat as an auth failure and log the user out.
  const handleUpdatePost = (postId, postData) => {
    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (String(post.id) === String(postId)) {
          return { ...post, ...postData }
        }
        return post
      })
    )
    setAllPosts(prevPosts =>
      prevPosts.map(post => {
        if (String(post.id) === String(postId)) {
          return { ...post, ...postData }
        }
        return post
      })
    )
  }

  // Stub — cannot delete another user's posts
  const handleDeletePost = async (post) => {
    console.warn("Delete post not allowed on another user's profile");
  }

  // Stub — cannot edit another user's posts
  const handleEditPost = (post) => {
    console.warn("Edit post not allowed on another user's profile");
  }

  const closeCreateEditModal = () => {
    setIsCreateEditModalOpen(false);
    setEditingPostData(null);
    setPendingFileType(null);
  }

  const handleCreatePost = async (postData) => {
    try {
      const response = await postsAPI.createPost(postData);
      const newPost = socialUtils.transformPostData(response?.data?.data || response?.data);
      if (newPost) {
        // Add new post to the beginning of the posts list
        setPosts(prevPosts => [newPost, ...prevPosts]);
        setAllPosts(prevPosts => [newPost, ...prevPosts]);
        // Update posts count
        if (profileData) {
          setProfileData(prev => ({
            ...prev,
            postsCount: (prev.postsCount || 0) + 1
          }));
        }
      }
      closeCreateEditModal();
    } catch (error) {
      console.error('Error creating post:', error);
      throw error;
    }
  }

  const handleLikeToggle = (postId, isLiked) => {
    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (post.id === postId) {
          const likesCount = post.likes_count || post.likesCount || 0
          return {
            ...post,
            is_liked: isLiked,
            likes_count: isLiked ? likesCount + 1 : Math.max(0, likesCount - 1)
          }
        }
        return post
      })
    )
  }

  const handleCommentAdded = (postId) => {
    setPosts(prevPosts =>
      prevPosts.map(post => {
        if (post.id === postId) {
          const commentsCount = post.comments_count || post.commentsCount || 0
          return {
            ...post,
            comments_count: commentsCount + 1
          }
        }
        return post
      })
    )
  }

  // Handle follow/unfollow
  const handleFollow = async () => {
    const previousConnectionStatus = connectionStatus
    try {
      if (isFollowing) {
        setShowUnfollowModal(true)
        return
      }

      if (confirmRequest) {
        setShowConfirmRequestModal(true)
        return
      }

      let action = "follow"
      if (isPendingFollow) {
        action = "cancel_request"
      }

      if (action === "follow") {
        applyConnectionStatusState('pending_outgoing')
      } else if (action === "cancel_request") {
        applyConnectionStatusState('none')
      }

      const token = localStorage.getItem("access_token")
      if (!token) {
        throw new Error("Authentication required")
      }

      if (!profileData || !profileData.id) {
        throw new Error("Profile data not available")
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const targetUserId = profileData.id

      const response = await fetch(
        `${API_BASE_URL}/api/v1/users/${targetUserId}/follow`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            action: action,
          }),
        }
      )

      if (response.status === 200) {
        const result = await response.json()
        if (result.status_message === "pending") {
          applyConnectionStatusState('pending_outgoing')
          if (currentUser) {
            const currentFollowing = currentUser.following_count || currentUser.stats?.following || 0
            updateUserData({
              following_count: currentFollowing + 1,
              stats: {
                ...(currentUser.stats || {}),
                following: currentFollowing + 1,
              },
            })
          }
        } else if (result.status_message === "unfollowed") {
          applyConnectionStatusState('none')
          if (currentUser) {
            const currentFollowing = currentUser.following_count || currentUser.stats?.following || 0
            const nextFollowing = Math.max(0, currentFollowing - 1)
            updateUserData({
              following_count: nextFollowing,
              stats: {
                ...(currentUser.stats || {}),
                following: nextFollowing,
              },
            })
          }
        } else if (result.status_message === "approved") {
          applyConnectionStatusState('friends')
        }
      } else {
        applyConnectionStatusState(previousConnectionStatus)
      }
    } catch (error) {
      console.error("Error updating follow status:", error)
      setError(t("otherProfile.errors.updateFollowStatus"))
      applyConnectionStatusState(previousConnectionStatus)
    }
  }

  const handleUnfollow = async () => {
    try {
      setShowUnfollowModal(false)

      const token = localStorage.getItem("access_token")
      if (!token) {
        throw new Error("Authentication required")
      }

      if (!profileData || !profileData.id) {
        throw new Error("Profile data not available")
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const targetUserId = profileData.id

      const response = await fetch(
        `${API_BASE_URL}/api/v1/users/${targetUserId}/unfollow`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (response.status === 200) {
        applyConnectionStatusState('none')
        setSuccessMessage(t("otherProfile.messages.unfollowed"))
        setTimeout(() => setSuccessMessage(null), 3000)
      }
    } catch (error) {
      console.error("Error unfollowing user:", error)
      setError(t("otherProfile.errors.unfollow"))
    }
  }

  const handleAcceptFollowRequest = async () => {
    try {
      setShowConfirmRequestModal(false)

      const token = localStorage.getItem("access_token")
      if (!token) {
        throw new Error("Authentication required")
      }

      if (!profileData || !profileData.id) {
        throw new Error("Profile data not available")
      }

      await socialAPI.acceptConnectionRequest(profileData.id)
      applyConnectionStatusState('friends')
      setSuccessMessage(t("otherProfile.messages.requestAccepted"))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error) {
      console.error("Error accepting follow request:", error)
      setError(t("otherProfile.errors.requestAccept"))
    }
  }

  const handleRejectFollowRequest = async () => {
    try {
      setShowConfirmRequestModal(false)

      const token = localStorage.getItem("access_token")
      if (!token) {
        throw new Error("Authentication required")
      }

      if (!profileData || !profileData.id) {
        throw new Error("Profile data not available")
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      const targetUserId = profileData.id

      const response = await fetch(
        `${API_BASE_URL}/api/v1/users/${targetUserId}/reject-follow`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )

      if (response.status === 200) {
        applyConnectionStatusState('none')
        setSuccessMessage(t("otherProfile.messages.requestRejected"))
        setTimeout(() => setSuccessMessage(null), 3000)
      }
    } catch (error) {
      console.error("Error rejecting follow request:", error)
      setError(t("otherProfile.errors.requestReject"))
    }
  }

  const handleBlock = () => {
    setShowBlockModal(true)
  }

  const confirmBlock = async () => {
    if (!profileData?.id) {
      return
    }

    try {
      await userAPI.blockUser(profileData.id)
      setIsBlockedUser(true)
      setProfileData((prev) => (prev ? { ...prev, is_blocked: true } : prev))
      setSuccessMessage(t("otherProfile.messages.blockedSuccess"))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error) {
      console.error("Error blocking user:", error)
      setError(t("otherProfile.errors.blockFailed"))
      setTimeout(() => setError(null), 5000)
    } finally {
      setShowBlockModal(false)
    }
  }

  const handleReport = async () => {
    if (!reportReason) return

    try {
      setIsSubmittingReport(true)
      await reportsAPI.reportUser(profileData.id, reportReason, reportDetails, blockUser, muteUser)

      setSuccessMessage(t("otherProfile.toasts.reportSuccess") || "User reported successfully")
      setTimeout(() => setSuccessMessage(null), 3000)

      setShowReportModal(false)
      setReportReason('')
      setReportDetails('')
      setBlockUser(false)
      setMuteUser(false)
      setCurrentStep(1)
    } catch (error) {
      console.error("Failed to report user:", error)
      setError(t("otherProfile.toasts.reportFailed") || "Failed to submit report")
      setTimeout(() => setError(null), 3000)
    } finally {
      setIsSubmittingReport(false)
    }
  }

  const handleUnblock = async () => {
    if (!profileData?.id) {
      return
    }

    try {
      await userAPI.unblockUser(profileData.id)
      setIsBlockedUser(false)
      setProfileData((prev) => (prev ? { ...prev, is_blocked: false } : prev))
      setSuccessMessage(t("otherProfile.messages.unblockedSuccess"))
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error) {
      console.error("Error unblocking user:", error)
      setError(t("otherProfile.errors.unblockFailed"))
      setTimeout(() => setError(null), 5000)
    }
  }



  // Close more menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreMenu && !event.target.closest('.userprofile-more-menu-container')) {
        setShowMoreMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMoreMenu])

  const handleMessage = () => {
    if (profileData) {
      navigate(`/chat/${profileData.id}/social`)
    }
  }

  // Get user type color
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
      'FIRM': '#1B3A6B', // Navy Blue
    }
    return userTypeConfig[userType] || '#8548A8'
  }

  // Use the viewed user's type, not the current user's type
  // Check profileData first, then fallback to userType from params, but never use currentUser's type
  const viewedUserType = normalizeUserType(profileData?.user_type || userType || null)
  const userTypeColor = getUserTypeColor(viewedUserType)

  if (isLoading) {
    return (
      <div className={`userprofile-root ${isRTL ? "rtl" : ""}`} dir={i18n.dir()} lang={i18n.language}>
        <Navbar userType={userType} />
        <div className="userprofile-loading">
          <div className="userprofile-loading-spinner"></div>
          <p>{t("common.loading") || "Loading..."}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`userprofile-root ${isRTL ? "rtl" : ""}`} dir={i18n.dir()} lang={i18n.language}>
        <Navbar userType={userType} />
        <div className="userprofile-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            {t("common.retry") || "Retry"}
          </button>
        </div>
      </div>
    )
  }

  if (!profileData) {
    return null
  }

  return (
    <div
      className={`userprofile-root ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
      lang={i18n.language}
      style={{ '--user-type-color': userTypeColor }}
    >
      <Navbar userType={userType} />

      {/* Success Message */}
      {successMessage && (
        <div className="userprofile-success-toast">
          <div className="userprofile-success-content">
            <span className="userprofile-success-text">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Full Width Layout */}
      <div className="userprofile-layout">
        {/* Left Sidebar Menu */}
        <div className="userprofile-sidebar-menu">
          {/* Profile Info in Sidebar removed as requested */}

          {/* Profile Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => goToTab(1)}
          >
            <div className="userprofile-menu-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.profile") || "Profile"}</h3>
            </div>
          </div>

          {/* Buddies Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 2 ? 'active' : ''} ${!canSeeBuddies() ? 'disabled' : ''}`}
            onClick={() => canSeeBuddies() && goToTab(2)}
            title={!canSeeBuddies() ? t("otherProfile.messages.buddiesHidden") : ""}
          >
            <div className="userprofile-menu-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6"/><path d="M23 11h-6"/></svg>
            </div>
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.buddies") || "Buddies"}</h3>
            </div>
          </div>

          {/* Pets Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 3 ? 'active' : ''}`}
            onClick={() => goToTab(3)}
          >
            <div className="userprofile-menu-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3.1-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"/><path d="M8 14v.5"/><path d="M16 14v.5"/><path d="M11.25 16.25h1.5"/></svg>
            </div>
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.pets") || "Pets"}</h3>
            </div>
          </div>

          {/* Communities Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 4 ? 'active' : ''}`}
            onClick={() => goToTab(4)}
          >
            <div className="userprofile-menu-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.communities") || "Communities"}</h3>
            </div>
          </div>

          {/* Media Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 5 ? 'active' : ''}`}
            onClick={() => goToTab(5)}
          >
            <div className="userprofile-menu-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
            </div>
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.media") || "Media"}</h3>
            </div>
          </div>

          {/* Posts Card */}
          <div
            className={`userprofile-menu-card ${activeTab === 6 ? 'active' : ''}`}
            onClick={() => goToTab(6)}
          >
            <div className="userprofile-menu-card-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            </div>
            <div className="userprofile-menu-card-header">
              <h3 className="userprofile-menu-card-title">{t("userprofile.menu.posts") || "Posts"}</h3>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="userprofile-main-content">
          {/* Profile Header Card - Always visible */}
          <div className="userprofile-header-card">
            {/* Top Section: User Info and Stats */}
            <div className="userprofile-header-top-section">
              {/* User Info on Left Side */}
              <div className="userprofile-header-user-info">
                <div className="userprofile-header-avatar-section">
                  <div className="userprofile-avatar-container">
                    <img
                      src={profileData.avatar || getUserPlaceholderImage(profileData)}
                      alt={profileData.name}
                      className="userprofile-avatar"
                      onError={(e) => {
                        const placeholder = getUserPlaceholderImage(profileData);
                        if (e.target.src !== placeholder) {
                          e.target.src = placeholder
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="userprofile-header-info">
                  <h1 className="userprofile-name">{profileData.name}</h1>
                  {/* Rating: show stars if rated, "New" badge if not */}
                  {profileData.sub_type !== 'rescuer' &&
                    !(profileData.sub_type?.toLowerCase() === 'rescuer' || profileData.sub_type_display_name?.toLowerCase() === 'rescuer') && (
                      (profileData.average_rating && parseFloat(profileData.average_rating) > 0) ? (
                        <UserAverageRating
                          averageRating={profileData.average_rating}
                          ratingsCount={profileData.ratings_count || 0}
                          size="medium"
                        />
                      ) : (
                        <span className="userprofile-new-badge">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                          {t("userprofile.newUser") || "New"}
                        </span>
                      )
                    )}
                  {profileData.user_type === 'PET_GUARDIAN' &&
                    (profileData.sub_type?.toLowerCase() === 'rescuer' || profileData.sub_type_display_name?.toLowerCase() === 'rescuer') && (
                      <p className="userprofile-subtype">Rescuer</p>
                    )}
                  {/* Show rating under subtype if user IS a rescuer */}
                  {(profileData.sub_type?.toLowerCase() === 'rescuer' || profileData.sub_type_display_name?.toLowerCase() === 'rescuer') && (
                    (profileData.average_rating && parseFloat(profileData.average_rating) > 0) ? (
                      <UserAverageRating
                        averageRating={profileData.average_rating}
                        ratingsCount={profileData.ratings_count || 0}
                        ratingLabel={profileData.rating_label}
                        size="medium"
                      />
                    ) : (
                      <span className="userprofile-new-badge">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        {t("userprofile.newUser") || "New"}
                      </span>
                    )
                  )}

                  {profileData.email && (
                    <div className="userprofile-detail-item">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{profileData.email}</span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="userprofile-actions-container">
                    {/* Follow Button */}
                    {canSendFollowRequest() && (
                      <button
                        onClick={handleFollow}
                        className={`userprofile-action-btn follow ${isFollowing ? 'following' :
                          confirmRequest ? 'confirm' :
                            isPendingFollow ? 'pending' : ''
                          }`}
                      >
                        {isFollowing ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <polyline points="17 11 19 13 23 9" />
                          </svg>
                        ) : isPendingFollow ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <circle cx="20" cy="11" r="3" />
                          </svg>
                        ) : confirmRequest ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="17" y1="11" x2="23" y2="11" />
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="20" y1="8" x2="20" y2="14" />
                            <line x1="17" y1="11" x2="23" y2="11" />
                          </svg>
                        )}
                        <span className="btn-text">
                          {isFollowing
                            ? t("otherProfile.actions.following")
                            : isPendingFollow
                              ? t("otherProfile.actions.pending")
                              : confirmRequest
                                ? t("otherProfile.actions.confirmRequest")
                                : t("otherProfile.actions.follow")}
                        </span>
                      </button>
                    )}

                    {/* Message Button */}
                    {canSendMessage() && (
                      <button
                        onClick={handleMessage}
                        className="userprofile-action-btn message"
                        aria-label={t("otherProfile.actions.message")}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span className="btn-text">{t("otherProfile.actions.message")}</span>
                      </button>
                    )}

                    {/* More Menu (Three Dots) */}
                    <div className="userprofile-more-menu-container">
                      <button
                        onClick={() => setShowMoreMenu(!showMoreMenu)}
                        className="userprofile-more-menu-btn"
                        aria-label="More options"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>

                      {showMoreMenu && (
                        <div className="userprofile-more-menu-dropdown">
                          <button
                            className="userprofile-more-menu-item"
                            onClick={() => {
                              setShowMoreMenu(false)
                              setShowReportModal(true)
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                              <line x1="4" y1="22" x2="4" y2="15" />
                            </svg>
                            <span>{t("otherProfile.actions.report") || "Report User"}</span>
                          </button>
                          <button
                            className={`userprofile-more-menu-item ${isBlockedUser ? 'unblock' : 'block'}`}
                            onClick={() => {
                              setShowMoreMenu(false)
                              if (isBlockedUser) {
                                handleUnblock()
                              } else {
                                handleBlock()
                              }
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                            </svg>
                            <span>
                              {isBlockedUser
                                ? t("otherProfile.actions.unblock")
                                : t("otherProfile.actions.block")}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stat Cards on Right Side */}
              <div className="userprofile-header-stats-cards">
                <div className="userprofile-stat-square-card" onClick={() => goToTab(3)} title={t("userprofile.stats.pets") || "Pets"}>
                  <div className="userprofile-stat-square-icon">🐾</div>
                  <div className="userprofile-stat-square-number">{profileData.pets?.length || 0}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.pets") || "Pets"}</div>
                </div>
                <div className="userprofile-stat-square-card" onClick={() => goToTab(6)} title={t("userprofile.stats.posts") || "Posts"}>
                  <div className="userprofile-stat-square-icon">📝</div>
                  <div className="userprofile-stat-square-number">{profileData.postsCount}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.posts") || "Posts"}</div>
                </div>
                <div className="userprofile-stat-square-card" onClick={() => goToTab(2)} title={t("userprofile.stats.connects") || "Buddies"}>
                  <div className="userprofile-stat-square-icon">👥</div>
                  <div className="userprofile-stat-square-number">{profileData.connectsCount}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.connects") || "Buddies"}</div>
                </div>
                <div className="userprofile-stat-square-card" onClick={() => goToTab(4)} title={t("userprofile.stats.communities") || "Communities"}>
                  <div className="userprofile-stat-square-icon">🏘️</div>
                  <div className="userprofile-stat-square-number">{communitiesCount}</div>
                  <div className="userprofile-stat-square-label">{t("userprofile.stats.communities") || "Communities"}</div>
                </div>
                {(profileData?.user_type === 'VETERINARIAN' || profileData?.user_type === 'VET') && (
                  <div className="userprofile-stat-square-card" onClick={() => goToTab(4)} title={t("userprofile.stats.clinics") || "Clinics"}>
                    <div className="userprofile-stat-square-icon">🏥</div>
                    <div className="userprofile-stat-square-number">{clinicsCount || 0}</div>
                    <div className="userprofile-stat-square-label">{t("userprofile.stats.clinics") || "Clinics"}</div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Mobile Tab Navbar - Only visible on mobile/tablet, positioned below header */}
          <div className="userprofile-mobile-navbar">
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 1 ? 'active' : ''}`}
              onClick={() => goToTab(1)}
            >
              {t("userprofile.menu.profile") || "Profile"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 2 ? 'active' : ''} ${!canSeeBuddies() ? 'disabled' : ''}`}
              onClick={() => canSeeBuddies() && goToTab(2)}
              title={!canSeeBuddies() ? t("otherProfile.messages.buddiesHidden") : ""}
            >
              {t("userprofile.menu.buddies") || "Buddies"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 3 ? 'active' : ''}`}
              onClick={() => goToTab(3)}
            >
              {t("userprofile.menu.pets") || "Pets"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 4 ? 'active' : ''}`}
              onClick={() => goToTab(4)}
            >
              {t("userprofile.menu.communities") || "Communities"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 5 ? 'active' : ''}`}
              onClick={() => goToTab(5)}
            >
              {t("userprofile.menu.media") || "Media"}
            </button>
            <button
              className={`userprofile-mobile-nav-tab ${activeTab === 6 ? 'active' : ''}`}
              onClick={() => goToTab(6)}
            >
              {t("userprofile.menu.posts") || "Posts"}
            </button>
          </div>

          {/* Content based on active tab */}
          {activeTab === 1 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card" style={{ minHeight: '1px' }}>
                {/* About Me Section */}
                <div className="userprofile-header-bio-section">
                  <div className="userprofile-bio-header">
                    <h3 className="userprofile-bio-title">{t("userprofile.sections.aboutMe")}</h3>
                  </div>
                  {profileData.bio && profileData.bio.trim() ? (
                    <>
                      <p className={`userprofile-bio-text ${!isBioExpanded ? 'truncated' : ''}`}>
                        {profileData.bio}
                      </p>
                      {profileData.bio.length > 180 && (
                        <button
                          className="userprofile-bio-read-more"
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                        >
                          {isBioExpanded
                            ? (t("userprofile.bio.showLess") || "Show less")
                            : (t("userprofile.bio.readMore") || "Read more")
                          }
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isBioExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                            <polyline points="6 9 12 15 18 9"/>
                          </svg>
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="userprofile-bio-empty">{t("userprofile.sections.noBioAvailable")}</p>
                  )}
                </div>

                {/* Personal Details Section - conditional on visibility */}
                {(profileData.gender || (profileData.age !== null && profileData.age !== undefined) || profileData.location || profileData.phone || (profileData.user_type === 'FIRM' && (profileData.legal_number || profileData.contact_info || profileData.address))) && (
                  <>
                    <div className="userprofile-section-divider"></div>
                    <div className="userprofile-details-section">
                      <h3 className="userprofile-bio-title" style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>{t("userprofile.sections.personalDetails")}</h3>
                    <div className="userprofile-details-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '15px'
                    }}>
                      {profileData.phone && (
                        <div className="userprofile-detail-card">
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.phone") || "Phone"}</span>
                            <span className="userprofile-detail-value">{profileData.phone}</span>
                          </div>
                        </div>
                      )}

                      {profileData.user_type === 'FIRM' && (
                        <>
                          {profileData.show_name_en && (
                            <div className="userprofile-detail-card">
                              <div className="userprofile-detail-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12 7H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12 11H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M12 15H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M7 11H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M7 15H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M15 11H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M15 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <div className="userprofile-detail-text">
                                <span className="userprofile-detail-label">{t("userprofile.fields.nameEn") || "Company Name (EN)"}</span>
                                <span className="userprofile-detail-value">{profileData.name_en || t("userprofile.fields.notProvided")}</span>
                              </div>
                            </div>
                          )}

                          {profileData.show_legal_number && (
                            <div className="userprofile-detail-card">
                              <div className="userprofile-detail-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <div className="userprofile-detail-text">
                                <span className="userprofile-detail-label">{t("userprofile.fields.legalNumber") || "Legal Number"}</span>
                                <span className="userprofile-detail-value">{profileData.legal_number || t("userprofile.fields.notProvided")}</span>
                              </div>
                            </div>
                          )}

                          {profileData.show_contact_info && (
                            <div className="userprofile-detail-card">
                              <div className="userprofile-detail-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 8L10.8906 13.2604C11.5624 13.7083 12.4376 13.7083 13.1094 13.2604L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <div className="userprofile-detail-text">
                                <span className="userprofile-detail-label">{t("userprofile.fields.contactInfo") || "Contact information"}</span>
                                <span className="userprofile-detail-value">{profileData.contact_info || t("userprofile.fields.notProvided")}</span>
                              </div>
                            </div>
                          )}

                          {profileData.show_address && (
                            <div className="userprofile-detail-card">
                              <div className="userprofile-detail-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <div className="userprofile-detail-text">
                                <span className="userprofile-detail-label">{t("userprofile.fields.address") || "Address"}</span>
                                <span className="userprofile-detail-value">{profileData.address || t("userprofile.fields.notProvided")}</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {profileData.user_type !== 'FIRM' && profileData.gender && (
                        <div className="userprofile-detail-card">
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                              <path d="M12 11V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M9 18H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.gender") || "Gender"}</span>
                            <span className="userprofile-detail-value">
                              {profileData.gender 
                                ? t(`userprofile.genderOptions.${profileData.gender === 'Prefer not to say' ? 'preferNot' : profileData.gender.toLowerCase()}`, profileData.gender) 
                                : profileData.gender}
                            </span>
                          </div>
                        </div>
                      )}

                      {profileData.user_type !== 'FIRM' && (profileData.age || profileData.age === 0) && (
                        <div className="userprofile-detail-card">
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                              <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.age") || "Age"}</span>
                            <span className="userprofile-detail-value">
                              {profileData.age === 0 ? (t("userprofile.fields.notSpecified") || "Not specified") : `${profileData.age} ${t("userprofile.fields.years") || "years"}`}
                            </span>
                          </div>
                        </div>
                      )}

                      {profileData.location && (
                        <div className="userprofile-detail-card">
                          <div className="userprofile-detail-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <div className="userprofile-detail-text">
                            <span className="userprofile-detail-label">{t("userprofile.fields.location") || "Location"}</span>
                            <span className="userprofile-detail-value">{profileData.location}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            </div>
          )}

          {activeTab === 2 && (
            <div className="userprofile-tab-panel">
              <BuddiesGridTab 
                userId={profileData?.id}
                currentUser={currentUser}
                isCurrentUserProfile={false}
                accentColor={userTypeColor}
              />
            </div>
          )}

          {activeTab === 3 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card">
                <div className="userprofile-section-header">
                  <h2 className="userprofile-section-title">
                    {t("userprofile.sections.theirPets") || t("userprofile.sections.myPets")} <span className="userprofile-section-count">({profileData.pets?.length || 0})</span>
                  </h2>
                </div>
                {profileData.pets && profileData.pets.length > 0 ? (
                  <div className="userprofile-pets-grid">
                    {(profileData.pets || []).map(pet => (
                      <div
                        key={pet.id}
                        className="userprofile-pet-card"
                        onClick={() => navigate(`/pets/view/others/${pet.id}/petProfile`, { state: { fromProfile: true, fromProfileTab: activeTab, profileId: targetUserId } })}
                      >
                        <img
                          src={pet.avatar || "/images/no-image-pet.png"}
                          alt={pet.name}
                          className="userprofile-pet-image"
                        />
                        <p className="userprofile-pet-name">{pet.name}</p>
                        {pet.type && (
                          <p className="userprofile-pet-type">{pet.type}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="userprofile-empty-state">
                    <span style={{ fontSize: '32px' }}>🐾</span>
                    <p>{t("userprofile.sections.noPets")}</p>
                  </div>
                )}
                {hasMorePets && (
                  <div className="userprofile-load-more-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button
                      className="userprofile-load-more-btn"
                      onClick={loadMorePets}
                      disabled={isLoadingMorePets}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#8548A8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: isLoadingMorePets ? 'not-allowed' : 'pointer',
                        opacity: isLoadingMorePets ? 0.6 : 1,
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {isLoadingMorePets
                        ? (t("userprofile.loading") || "Loading...")
                        : (t("userprofile.loadMorePets") || "Load More Pets")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 4 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card">
                <div className="userprofile-section-header">
                  <h2 className="userprofile-section-title">{t("userprofile.sections.communities")}</h2>
                </div>
                {isLoadingCommunities ? (
                  <p>{t("userprofile.sections.loadingCommunities")}</p>
                ) : communities.length > 0 ? (
                  <div className="userprofile-communities-grid">
                    {communities.map(community => (
                      <div
                        key={community.id}
                        className="userprofile-community-card"
                        onClick={() => navigate(`/social/channels/${community.id}`, { state: { fromProfile: true, fromProfileTab: activeTab, profileId: targetUserId } })}
                      >
                        <img
                          src={community.profileImage}
                          alt={community.name}
                          className="userprofile-community-image"
                        />
                        <h3 className="userprofile-community-name">{community.name}</h3>
                        {community.description && (
                          <p className="userprofile-community-description">{community.description}</p>
                        )}
                        <div className="userprofile-community-stats">
                          <span>{community.memberCount} {t("userprofile.sections.members")}</span>
                          <span>{community.postCount} {t("userprofile.sections.posts")}</span>
                        </div>
                        <div className="userprofile-community-action">
                          {t("common.view") || "View"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="userprofile-empty-state">
                    <span style={{ fontSize: '32px' }}>🏘️</span>
                    <p>{t("userprofile.sections.noPublicCommunities")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 5 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card">
                <div className="userprofile-media-gallery">
                  {isLoadingMedia ? (
                    <div className="userprofile-media-loading">
                      <p>{t("common.loading") || "Loading..."}</p>
                    </div>
                  ) : mediaItems.length > 0 ? (
                    <div className="userprofile-media-grid">
                      {mediaItems.map((mediaItem, index) => {
                        const mediaUrl = resolveImageUrl(
                          mediaItem.url,
                          mediaItem.path,
                          mediaItem.file_path,
                          mediaItem.media_path
                        )
                        const isVideo = mediaItem.file_type === 'video' ||
                          mediaItem.type === 'video' ||
                          (typeof mediaUrl === 'string' &&
                            (mediaUrl.includes('.mp4') ||
                              mediaUrl.includes('.mov') ||
                              mediaUrl.includes('.webm')))



                        return (
                          <div
                            key={mediaItem.id || index}
                            className="userprofile-media-item"
                            onClick={() => {
                              if (mediaItem.postId) {
                                const basePath = getBaseProfilePath();
                                const tabName = REVERSE_TAB_MAP[activeTab];
                                navigate(`${basePath}/${tabName}/${mediaItem.postId}`);
                              }
                            }}
                          >
                            {isVideo ? (
                              <video
                                src={mediaUrl}
                                className="userprofile-media-thumbnail"
                                muted
                              />
                            ) : (
                              <img
                                src={mediaUrl}
                                alt={mediaItem.postContent || "Post media"}
                                className="userprofile-media-thumbnail"
                                loading="lazy"
                              />
                            )}
                            {isVideo && (
                              <div className="userprofile-media-play-icon">
                                ▶
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="userprofile-media-empty">
                      <span style={{ fontSize: '40px', marginBottom: '16px', opacity: 0.5 }}>📷</span>
                      <p>{t("userprofile.noMedia") || "No media posted yet"}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 6 && (
            <div className="userprofile-tab-panel">
              <div className="userprofile-section-card">
                <div className="userprofile-posts-container">
                  {isLoadingPosts ? (
                    <div className="userprofile-posts-loading">
                      <p>{t("common.loading") || "Loading posts..."}</p>
                    </div>
                  ) : posts && posts.length > 0 ? (
                    <>
                      <PostFeed
                        posts={posts}
                        currentUser={currentUser}
                        onUpdatePost={handleUpdatePost}
                        onDeletePost={handleDeletePost}
                        onEditPost={handleEditPost}
                        onLikeToggle={handleLikeToggle}
                        onCommentAdded={handleCommentAdded}
                        onLoadMore={null}
                        hasMorePosts={false}
                        isLoading={false}
                      />
                      {hasMorePosts && (
                        <div className="userprofile-load-more-container">
                          <button
                            className="userprofile-load-more-btn"
                            onClick={loadMorePosts}
                            disabled={isLoadingMorePosts}
                          >
                            {isLoadingMorePosts ? (
                              <>
                                <span className="userprofile-load-more-spinner"></span>
                                {t("common.loading") || "Loading..."}
                              </>
                            ) : (
                              t("userprofile.loadMorePosts") || "Load More Posts"
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="userprofile-posts-empty">
                      <span style={{ fontSize: '40px', marginBottom: '16px', opacity: 0.5 }}>📝</span>
                      <p>{t("userprofile.noPosts") || "No posts yet"}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Connects Modal removed, now a tab */}

      {/* Unfollow Confirmation Modal */}
      {showUnfollowModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setShowUnfollowModal(false)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 16px auto",
                background: "#FEF2F2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#EF4444">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h3
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "20px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0 0 8px 0",
              }}
            >
              {t("otherProfile.modals.unfollow.title", {
                name: profileData?.name || t("otherProfile.fallbackName"),
              })}
            </h3>
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                color: "#8B8B8B",
                margin: "0 0 24px 0",
                lineHeight: "1.5",
              }}
            >
              {t("otherProfile.modals.unfollow.description")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => setShowUnfollowModal(false)}
                style={{
                  padding: "12px 24px",
                  background: "#F3F4F6",
                  color: "#6B7280",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("userprofile.buttons.cancel")}
              </button>
              <button
                onClick={handleUnfollow}
                style={{
                  padding: "12px 24px",
                  background: "#EF4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("otherProfile.modals.unfollow.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Confirmation Modal */}
      {showBlockModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setShowBlockModal(false)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 16px auto",
                background: "#FEF2F2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#EF4444">
                <path d="M12 2a10 10 0 0 0-8 3.5l12.5 12.5A10 10 0 0 0 22 12a10 10 0 0 0-10-10zm0 18a7.96 7.96 0 0 1-5.66-2.34L16.66 6.34A7.96 7.96 0 0 1 12 20z" />
              </svg>
            </div>
            <h3
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "20px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0 0 8px 0",
              }}
            >
              {t("otherProfile.modals.block.title", {
                name: profileData?.name || t("otherProfile.fallbackName"),
              })}
            </h3>
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                color: "#8B8B8B",
                margin: "0 0 24px 0",
                lineHeight: "1.5",
              }}
            >
              {t("otherProfile.modals.block.description")}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => setShowBlockModal(false)}
                style={{
                  padding: "12px 24px",
                  background: "#F3F4F6",
                  color: "#6B7280",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("userprofile.buttons.cancel")}
              </button>
              <button
                onClick={confirmBlock}
                style={{
                  padding: "12px 24px",
                  background: "#EF4444",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("otherProfile.actions.block")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Request Modal */}
      {showConfirmRequestModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setShowConfirmRequestModal(false)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 16px auto",
                background: "#EFF6FF",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="#3B82F6">
                <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A2.998 2.998 0 0 0 17.12 7H16.5c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5H17v6h3zm-12.5-3.5c.83 0 1.5-.67 1.5-1.5v-6c0-.83-.67-1.5-1.5-1.5H6.88a2.998 2.998 0 0 0-2.84 1.37L1.5 16H4v6h3.5v-3.5zm5.5-9.5c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2z" />
              </svg>
            </div>
            <h3
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "20px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0 0 8px 0",
              }}
            >
              {t("otherProfile.modals.request.title", {
                name: profileData?.name || t("otherProfile.fallbackName"),
              })}
            </h3>
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "16px",
                color: "#8B8B8B",
                margin: "0 0 24px 0",
                lineHeight: "1.5",
              }}
            >
              {t("otherProfile.modals.request.description", {
                name: profileData?.name || t("otherProfile.fallbackName"),
              })}
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                onClick={handleRejectFollowRequest}
                style={{
                  padding: "12px 24px",
                  background: "#F3F4F6",
                  color: "#6B7280",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("otherProfile.modals.request.reject")}
              </button>
              <button
                onClick={handleAcceptFollowRequest}
                style={{
                  padding: "12px 24px",
                  background: "#3B82F6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("otherProfile.modals.request.accept")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report User Modal */}
      {showReportModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setShowReportModal(false)}
        >
          <div
            style={{
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "450px",
              width: "100%",
              textAlign: "center",
              overflow: "hidden", // Important for sliding effect
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Icon */}
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 16px auto",
                background: "#FEF2F2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                <line x1="4" y1="22" x2="4" y2="15" />
              </svg>
            </div>

            <h3
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "20px",
                fontWeight: 600,
                color: "#303E4E",
                margin: "0 0 8px 0",
              }}
            >
              {t("otherProfile.modals.report.title") || "Report User"}
            </h3>

            {/* Content Area with Sliding Steps */}
            <div style={{ position: 'relative', minHeight: '450px' }}>

              {/* Step 1: Reason Selection */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  opacity: currentStep === 1 ? 1 : 0,
                  transform: currentStep === 1 ? 'translateX(0)' : 'translateX(-50px)',
                  pointerEvents: currentStep === 1 ? 'auto' : 'none',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#8B8B8B",
                    margin: "0 0 24px 0",
                    lineHeight: "1.5",
                  }}
                >
                  {t("otherProfile.modals.report.description") || "Please select a reason for reporting this user."}
                </p>

                {/* Report Reasons List */}
                <div style={{ textAlign: "left", marginBottom: "16px", flex: 1, overflowY: 'auto' }}>
                  {isLoadingReportReasons ? (
                    <div style={{ textAlign: "center", padding: "20px", color: "#6B7280" }}>
                      {t("common.loading") || "Loading..."}
                    </div>
                  ) : (
                    reportReasonsList.map((reason) => (
                      <label
                        key={reason.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px",
                          marginBottom: "8px",
                          background: reportReason === reason.value ? "#F3F4F6" : "transparent",
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                      >
                        <input
                          type="radio"
                          name="reportReason"
                          value={reason.value}
                          checked={reportReason === reason.value}
                          onChange={(e) => setReportReason(e.target.value)}
                          style={{
                            width: "18px",
                            height: "18px",
                            accentColor: "#3B82F6",
                          }}
                        />
                        <span
                          style={{
                            fontFamily: '"Red Hat Display", sans-serif',
                            fontSize: "14px",
                            color: "#303E4E",
                          }}
                        >
                          {reason.reasonText || reason.label}
                        </span>
                      </label>
                    ))
                  )}
                </div>

                {/* Additional Details */}
                <textarea
                  placeholder={t("otherProfile.modals.report.detailsPlaceholder") || "Additional details (optional)"}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "60px",
                    padding: "12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    resize: "vertical",
                    marginBottom: "24px",
                    boxSizing: "border-box",
                  }}
                />

                {/* Step 1 Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                    marginTop: "auto"
                  }}
                >
                  <button
                    onClick={() => {
                      setShowReportModal(false)
                      setReportReason('')
                      setReportDetails('')
                    }}
                    style={{
                      padding: "12px 24px",
                      background: "#F3F4F6",
                      color: "#6B7280",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("userprofile.buttons.cancel")}
                  </button>
                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!reportReason}
                    style={{
                      padding: "12px 24px",
                      background: !reportReason ? "#D1D5DB" : "#3B82F6",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: !reportReason ? "not-allowed" : "pointer",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("common.next") || "Next"}
                  </button>
                </div>
              </div>

              {/* Step 2: Additional Actions */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  opacity: currentStep === 2 ? 1 : 0,
                  transform: currentStep === 2 ? 'translateX(0)' : 'translateX(50px)',
                  pointerEvents: currentStep === 2 ? 'auto' : 'none',
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#303E4E",
                    margin: "0 0 16px 0",
                  }}
                >
                  {t("otherProfile.modals.report.additionalActions") || "Additional Actions"}
                </p>
                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#8B8B8B",
                    margin: "0 0 24px 0",
                    lineHeight: "1.5",
                  }}
                >
                  {t("otherProfile.modals.report.actionsDescription") || "Would you like to take further action against this user?"}
                </p>

                {/* Block Option */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px",
                    background: blockUser ? "#FEF2F2" : "#F9FAFB",
                    border: blockUser ? "1px solid #FCA5A5" : "1px solid #E5E7EB",
                    borderRadius: "12px",
                    cursor: "pointer",
                    marginBottom: "12px",
                    transition: "all 0.2s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={blockUser}
                    onChange={(e) => {
                      setBlockUser(e.target.checked)
                      if (e.target.checked) setMuteUser(false)
                    }}
                    style={{ width: "20px", height: "20px", accentColor: "#EF4444" }}
                  />
                  <div style={{ textAlign: "left" }}>
                    <span style={{ display: "block", fontWeight: 600, color: "#1F2937", marginBottom: "4px" }}>
                      {t("otherProfile.modals.report.blockUser") || "Block User"}
                    </span>
                    <span style={{ fontSize: "12px", color: "#6B7280" }}>
                      {t("otherProfile.modals.report.blockDescription") || "You won't see their posts or be able to message them."}
                    </span>
                  </div>
                </label>

                {/* Mute Option */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px",
                    background: muteUser ? "#EFF6FF" : "#F9FAFB",
                    border: muteUser ? "1px solid #93C5FD" : "1px solid #E5E7EB",
                    borderRadius: "12px",
                    cursor: "pointer",
                    marginBottom: "12px",
                    transition: "all 0.2s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={muteUser}
                    onChange={(e) => {
                      setMuteUser(e.target.checked)
                      if (e.target.checked) setBlockUser(false)
                    }}
                    style={{ width: "20px", height: "20px", accentColor: "#3B82F6" }}
                  />
                  <div style={{ textAlign: "left" }}>
                    <span style={{ display: "block", fontWeight: 600, color: "#1F2937", marginBottom: "4px" }}>
                      {t("otherProfile.modals.report.muteUser") || "Mute User"}
                    </span>
                    <span style={{ fontSize: "12px", color: "#6B7280" }}>
                      {t("otherProfile.modals.report.muteDescription") || "You won't see their posts but they can still message you."}
                    </span>
                  </div>
                </label>

                {/* No Action Option */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px",
                    background: (!blockUser && !muteUser) ? "#F3F4F6" : "#F9FAFB",
                    border: (!blockUser && !muteUser) ? "1px solid #D1D5DB" : "1px solid #E5E7EB",
                    borderRadius: "12px",
                    cursor: "pointer",
                    marginBottom: "24px",
                    transition: "all 0.2s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!blockUser && !muteUser}
                    onChange={() => {
                      setBlockUser(false)
                      setMuteUser(false)
                    }}
                    style={{ width: "20px", height: "20px", accentColor: "#6B7280" }}
                  />
                  <div style={{ textAlign: "left" }}>
                    <span style={{ display: "block", fontWeight: 600, color: "#1F2937", marginBottom: "4px" }}>
                      {t("otherProfile.modals.report.noAction") || "No further action"}
                    </span>
                    <span style={{ fontSize: "12px", color: "#6B7280" }}>
                      {t("otherProfile.modals.report.noActionDescription") || "Just submit the report."}
                    </span>
                  </div>
                </label>

                {/* Step 2 Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                    marginTop: "auto"
                  }}
                >
                  <button
                    onClick={() => setCurrentStep(1)}
                    style={{
                      padding: "12px 24px",
                      background: "#F3F4F6",
                      color: "#6B7280",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {t("otherProfile.modals.report.back") || "Back"}
                  </button>
                  <button
                    onClick={handleReport}
                    disabled={isSubmittingReport}
                    style={{
                      padding: "12px 24px",
                      background: isSubmittingReport ? "#9CA3AF" : "#EF4444",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: isSubmittingReport ? "not-allowed" : "pointer",
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "14px",
                      fontWeight: 500,
                      minWidth: "120px"
                    }}
                  >
                    {isSubmittingReport ? (t("otherProfile.modals.report.submitting") || "Submitting...") : (t("otherProfile.modals.report.submit") || "Submit Report")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post Modal */}
      <PostModal
        isOpen={isCreateEditModalOpen}
        onClose={closeCreateEditModal}
        mode={editingPostData ? "edit" : "create"}
        editingPost={editingPostData}
        currentUser={currentUser}
        onCreatePost={handleCreatePost}
        onUpdatePost={handleUpdatePost}
        initialPrivacy={selectedPrivacy}
        pendingFileType={pendingFileType}
      />
    </div>
  )
}

export default OtherUsersProfile
